import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Edit, 
  Save, 
  X, 
  Search,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { ManualTestTrade, ManualTestStats } from '../types'
import {
  getManualTestTrades,
  getManualTestStats,
  updateManualTestTrade
} from '../lib/api'
import { supabase } from '../lib/supabase'


interface ManualTestDetailPageProps {
  strategyId?: string
}

export default function ManualTestDetailPage({ strategyId: propStrategyId }: ManualTestDetailPageProps) {
  const { strategyId: paramStrategyId } = useParams<{ strategyId: string }>()
  const strategyId = propStrategyId || paramStrategyId
  const navigate = useNavigate()


  const [trades, setTrades] = useState<ManualTestTrade[]>([])
  const [stats, setStats] = useState<ManualTestStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [strategyName, setStrategyName] = useState('')
  const [editingTrade, setEditingTrade] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Partial<ManualTestTrade>>({})
  
  // 筛选状态
  const [searchTerm, setSearchTerm] = useState('')
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  })
  const [filterDirection, setFilterDirection] = useState<'all' | 'long' | 'short'>('all')
  const [filterPnL, setFilterPnL] = useState<'all' | 'profit' | 'loss'>('all')
  const [filterRValueRange, setFilterRValueRange] = useState<string>('all')
  const [filterManualOverride, setFilterManualOverride] = useState<'all' | 'true' | 'false'>('all')
  const [sortBy, setSortBy] = useState<'entry_time' | 'pnl_amount' | 'pnl_r' | 'created_at'>('entry_time')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // 获取策略信息
  const fetchStrategyInfo = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('No session found')
      }

      const { data, error } = await supabase
        .from('strategies')
        .select('name, market_symbol, timeframe')
        .eq('id', strategyId)
        .single()

      if (error) throw error
      setStrategyName(data.name)
    } catch (error) {
      console.error('获取策略信息失败:', error)
    }
  }

  // 获取交易记录
  const fetchTrades = async () => {
    setLoading(true)
    try {
      const data = await getManualTestTrades(strategyId!)
      
      // 客户端筛选
      let filteredData = data || []
      
      // 应用筛选条件
      if (filterDirection !== 'all') {
        filteredData = filteredData.filter((trade: any) => trade.direction === filterDirection)
      }
      if (filterPnL === 'profit') {
        filteredData = filteredData.filter((trade: any) => trade.pnl_amount > 0)
      } else if (filterPnL === 'loss') {
        filteredData = filteredData.filter((trade: any) => trade.pnl_amount < 0)
      }
      if (filterManualOverride === 'true') {
        filteredData = filteredData.filter((trade: any) => trade.manual_override === true)
      } else if (filterManualOverride === 'false') {
        filteredData = filteredData.filter((trade: any) => trade.manual_override === false)
      }
      if (dateRange.start) {
        filteredData = filteredData.filter((trade: any) => trade.entry_time >= dateRange.start)
      }
      if (dateRange.end) {
        filteredData = filteredData.filter((trade: any) => trade.entry_time <= dateRange.end)
      }
      if (searchTerm) {
        filteredData = filteredData.filter((trade: any) =>
          trade.reason_entry.toLowerCase().includes(searchTerm.toLowerCase()) ||
          trade.reason_exit.toLowerCase().includes(searchTerm.toLowerCase()) ||
          trade.notes?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }

      if (filterRValueRange !== 'all') {
        const rangeMap: Record<string, { min: number; max: number }> = {
          'negative': { min: -Infinity, max: 0 },
          'small_positive': { min: 0, max: 1 },
          'medium_positive': { min: 1, max: 2 },
          'large_positive': { min: 2, max: Infinity }
        }
        const range = rangeMap[filterRValueRange]
        if (range) {
                  filteredData = filteredData.filter((trade: any) =>
          trade.pnl_r >= range.min && trade.pnl_r < range.max
        )
        }
      }

      // 应用排序
      filteredData.sort((a: any, b: any) => {
        const aValue = a[sortBy]
        const bValue = b[sortBy]
        if (sortOrder === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
        }
      })

      setTrades(filteredData)
    } catch (error) {
      console.error('获取交易记录失败:', error)
      // 如果获取交易记录失败，设置空数组
      setTrades([])
      console.debug('Trades loading failed, using empty array')
    } finally {
      setLoading(false)
    }
  }

  // 获取统计数据
  const fetchStats = async () => {
    try {
      const data = await getManualTestStats(strategyId!)
      setStats(data)
    } catch (error) {
      console.error('获取统计数据失败:', error)
      // 如果获取统计数据失败，设置默认值
      setStats({
        total_trades: 0,
        winning_trades: 0,
        losing_trades: 0,
        win_rate: 0,
        avg_r_value: 0,
        max_r_value: 0,
        min_r_value: 0,
        max_drawdown: 0,
        total_pnl: 0,
        avg_pnl_per_trade: 0,
        long_trades: 0,
        short_trades: 0,
        long_win_rate: 0,
        short_win_rate: 0,
        avg_trade_duration: 0,
        manual_override_count: 0,
        manual_override_rate: 0
      })
    }
  }

  useEffect(() => {
    if (strategyId) {
      fetchStrategyInfo()
      fetchTrades()
      fetchStats()
    }
  }, [strategyId, filterDirection, filterPnL, filterManualOverride, dateRange, sortBy, sortOrder])

  useEffect(() => {
    fetchTrades()
  }, [searchTerm, filterRValueRange])

  // 开始编辑
  const startEdit = (trade: ManualTestTrade) => {
    setEditingTrade(trade.id)
    setEditForm({
      entry_time: trade.entry_time,
      exit_time: trade.exit_time,
      direction: trade.direction,
      entry_price: trade.entry_price,
      exit_price: trade.exit_price,
      size: trade.size,
      reason_entry: trade.reason_entry,
      reason_exit: trade.reason_exit,
      manual_override: trade.manual_override,
      notes: trade.notes
    })
  }

  // 保存编辑
  const saveEdit = async () => {
    if (!editingTrade) return

    setLoading(true)
    try {
      const { pnl, pnlR } = calculatePnL(editForm)
      
      await updateManualTestTrade(editingTrade, {
        ...editForm,
        pnl_amount: pnl,
        pnl_r: pnlR
      })

      setEditingTrade(null)
      setEditForm({})
      fetchTrades()
      fetchStats()
    } catch (error) {
      console.error('更新交易记录失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 取消编辑
  const cancelEdit = () => {
    setEditingTrade(null)
    setEditForm({})
  }

  // 计算盈亏和R值
  const calculatePnL = (trade: Partial<ManualTestTrade>) => {
    const { entry_price, exit_price, size, direction } = trade
    if (!entry_price || !exit_price || !size || !direction) return { pnl: 0, pnlR: 0 }

    let pnl = 0
    if (direction === 'long') {
      pnl = (exit_price - entry_price) * size
    } else {
      pnl = (entry_price - exit_price) * size
    }

    // 这里假设初始风险为1，实际应该从数据库获取或用户输入
    const pnlR = pnl / 1
    return { pnl, pnlR }
  }

  // 获取交易持续时间
  const getTradeDuration = (entryTime: string, exitTime: string) => {
    const entry = new Date(entryTime)
    const exit = new Date(exitTime)
    const diffMs = exit.getTime() - entry.getTime()
    const diffHours = diffMs / (1000 * 60 * 60)
    
    if (diffHours < 1) {
      return `${Math.round(diffMs / (1000 * 60))}分钟`
    } else if (diffHours < 24) {
      return `${Math.round(diffHours)}小时`
    } else {
      return `${Math.round(diffHours / 24)}天`
    }
  }

  // 获取状态图标
  const getStatusIcon = (trade: ManualTestTrade) => {
    if (trade.manual_override) {
      return <AlertTriangle className="w-4 h-4 text-yellow-500" />
    }
    return trade.pnl_amount >= 0 ? 
      <CheckCircle className="w-4 h-4 text-green-500" /> : 
      <XCircle className="w-4 h-4 text-red-500" />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 头部 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-md hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {strategyName} - 手动测试详细记录
                </h1>
                <p className="text-sm text-gray-500">
                  共 {trades.length} 笔交易记录
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 统计摘要 */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Target className="w-5 h-5 text-blue-600" />
                <span className="ml-2 text-sm text-gray-600">总交易</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.total_trades}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <span className="ml-2 text-sm text-gray-600">胜率</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{(stats.win_rate * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <DollarSign className="w-5 h-5 text-purple-600" />
                <span className="ml-2 text-sm text-gray-600">总盈亏</span>
              </div>
              <p className={`text-2xl font-bold ${stats.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stats.total_pnl >= 0 ? '+' : ''}{stats.total_pnl.toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Target className="w-5 h-5 text-indigo-600" />
                <span className="ml-2 text-sm text-gray-600">平均R值</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.avg_r_value.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-orange-600" />
                <span className="ml-2 text-sm text-gray-600">平均持仓</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.avg_trade_duration.toFixed(1)}h</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <span className="ml-2 text-sm text-gray-600">干预率</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{(stats.manual_override_rate * 100).toFixed(1)}%</p>
            </div>
          </div>
        )}

        {/* 筛选器 */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">筛选条件</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* 搜索 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">搜索</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="搜索入场/出场理由或备注..."
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* 日期范围 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">日期范围</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* 交易方向 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">交易方向</label>
                <select
                  value={filterDirection}
                  onChange={(e) => setFilterDirection(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">所有方向</option>
                  <option value="long">多头</option>
                  <option value="short">空头</option>
                </select>
              </div>

              {/* 盈亏状态 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">盈亏状态</label>
                <select
                  value={filterPnL}
                  onChange={(e) => setFilterPnL(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">所有盈亏</option>
                  <option value="profit">盈利</option>
                  <option value="loss">亏损</option>
                </select>
              </div>

              {/* R值范围 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">R值范围</label>
                <select
                  value={filterRValueRange}
                  onChange={(e) => setFilterRValueRange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">所有R值</option>
                  <option value="negative">负R值</option>
                  <option value="small_positive">小正R值 (0-1)</option>
                  <option value="medium_positive">中正R值 (1-2)</option>
                  <option value="large_positive">大正R值 (2+)</option>
                </select>
              </div>

              {/* 主观干预 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">主观干预</label>
                <select
                  value={filterManualOverride}
                  onChange={(e) => setFilterManualOverride(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">所有记录</option>
                  <option value="true">有干预</option>
                  <option value="false">无干预</option>
                </select>
              </div>

              {/* 排序 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">排序</label>
                <div className="flex gap-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="entry_time">入场时间</option>
                    <option value="pnl_amount">盈亏金额</option>
                    <option value="pnl_r">R值</option>
                    <option value="created_at">创建时间</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 交易记录列表 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">交易记录详情</h3>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">加载中...</p>
            </div>
          ) : trades.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              暂无符合条件的交易记录
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      时间信息
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      交易方向
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      价格信息
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      盈亏信息
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      交易理由
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trades.map((trade) => (
                    <tr key={trade.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusIcon(trade)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">
                            {new Date(trade.entry_time).toLocaleDateString()}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {new Date(trade.entry_time).toLocaleTimeString()} - {new Date(trade.exit_time).toLocaleTimeString()}
                          </div>
                          <div className="text-gray-500 text-xs">
                            持仓: {getTradeDuration(trade.entry_time, trade.exit_time)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          trade.direction === 'long' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {trade.direction === 'long' ? (
                            <TrendingUp className="w-3 h-3 mr-1" />
                          ) : (
                            <TrendingDown className="w-3 h-3 mr-1" />
                          )}
                          {trade.direction === 'long' ? '多头' : '空头'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div>入场: {trade.entry_price}</div>
                          <div>出场: {trade.exit_price}</div>
                          <div className="text-gray-500 text-xs">数量: {trade.size}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className={`font-medium ${trade.pnl_amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {trade.pnl_amount >= 0 ? '+' : ''}{trade.pnl_amount.toFixed(2)}
                          </div>
                          <div className={`text-xs ${trade.pnl_r >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {trade.pnl_r >= 0 ? '+' : ''}{trade.pnl_r.toFixed(2)}R
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs">
                          <div className="font-medium text-xs text-gray-500 mb-1">入场理由:</div>
                          <div className="text-xs mb-2">{trade.reason_entry}</div>
                          <div className="font-medium text-xs text-gray-500 mb-1">出场理由:</div>
                          <div className="text-xs">{trade.reason_exit}</div>
                          {trade.notes && (
                            <>
                              <div className="font-medium text-xs text-gray-500 mb-1 mt-2">备注:</div>
                              <div className="text-xs">{trade.notes}</div>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {editingTrade === trade.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={saveEdit}
                              className="text-green-600 hover:text-green-900"
                              disabled={loading}
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEdit(trade)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 